import axios from 'axios';
import config from '../config';

export const API_URL = config.api.baseUrl;
export const WEBSOCKET_URL = config.api.websocketUrl;

// Function to get CSRF token from cookies
const getCsrfToken = () => {
    const name = 'XSRF-TOKEN';
    const match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
    const token = match ? decodeURIComponent(match[2]) : null;
    return token && token.trim() !== '' ? token : null;
};

// Create the main axios instance
const axiosInstance = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN'
});

// Global refresh state
let refreshPromise = null;

// Request interceptor - add CSRF token
axiosInstance.interceptors.request.use(
    (config) => {
        const token = getCsrfToken();
        if (token) {
            config.headers['X-XSRF-TOKEN'] = token;
        }
        
        if (config.data && !(config.data instanceof FormData) && !config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }
        
        return config;
    },
    (error) => Promise.reject(error)
);

// Helper function to refresh token - uses raw axios to avoid interceptor issues
async function refreshToken() {
    if (!refreshPromise) {
        refreshPromise = axios.post(`${API_URL}/auth/refresh`, null, { withCredentials: true })
            .finally(() => {
                setTimeout(() => {
                    refreshPromise = null;
                }, 1000);
            });
    }
    return refreshPromise;
}

// Wrapper that handles 401 retry logic OUTSIDE of interceptors
async function requestWithRetry(requestConfig) {
    try {
        const response = await axiosInstance.request(requestConfig);
        return response;
    } catch (error) {
        // Only retry on 401, not already retried, not a refresh request
        const url = requestConfig.url || '';
        if (
            error.response?.status === 401 &&
            !requestConfig._retry &&
            url !== '/auth/refresh' &&
            (!url.startsWith('/auth/') || url === '/auth/me')
        ) {
            requestConfig._retry = true;
            
            try {
                await refreshToken();
                
                // Wait for browser to process cookies
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Update CSRF token
                const token = getCsrfToken();
                if (token) {
                    requestConfig.headers = requestConfig.headers || {};
                    requestConfig.headers['X-XSRF-TOKEN'] = token;
                }
                
                const retryResponse = await axiosInstance.request(requestConfig);
                return retryResponse;
            } catch (refreshError) {
                throw error; // Throw original error
            }
        }
        
        throw error;
    }
}

// Create the API object with methods that use the retry wrapper
export const api = {
    get: (url, config = {}) => requestWithRetry({ ...config, method: 'get', url }),
    post: (url, data, config = {}) => requestWithRetry({ ...config, method: 'post', url, data }),
    put: (url, data, config = {}) => requestWithRetry({ ...config, method: 'put', url, data }),
    patch: (url, data, config = {}) => requestWithRetry({ ...config, method: 'patch', url, data }),
    delete: (url, config = {}) => requestWithRetry({ ...config, method: 'delete', url }),
    request: (config) => requestWithRetry(config),
    
    // Expose the raw axios instance for cases that need it
    defaults: axiosInstance.defaults,
    interceptors: axiosInstance.interceptors,
};

export default api;